import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { catchError, map, Observable, throwError } from 'rxjs';
import { Configuration } from '../configuration';
import { Policies } from '../models/policies.model';
import { PolicyOverviewResponse } from '../models/policyOverviewResponse.model';
import { PolicySummaryResponse } from '../models/policySummaryResponse.model';
import { DocumentsVm } from '../models/quotationSummaryResponse.model';

@Injectable({
  providedIn: 'root'
})
export class PoliciesService {
  public configuration = new Configuration();
  baseUrl: string;
  apiEndpoint: string = 'policies';

  constructor(private http: HttpClient, configuration: Configuration) {
    this.baseUrl = configuration.apiBasePath ?? '';
  }

  public createPolicy(body: Policies) {
    return this.http.post(`${this.baseUrl}${this.apiEndpoint}`, body);
  }

  public getPolicyDocument(id: Guid) {
    return this.http.get<DocumentsVm[]>(`${this.baseUrl}${this.apiEndpoint}/V2/${id}/documents`);
  }

  public startSignature(id: Guid, phonePrefix: string, phoneNumber: string) {
    const body = {
      policyAgreementId: id,
      phonePrefix: phonePrefix,
      phoneNumber: phoneNumber
    };
    return this.http.post(`${this.baseUrl}${this.apiEndpoint}/signature:start`, body);
  }

  public completeSignature(id: Guid, signatureNumber: string, pinCode: string) {
    const body = {
      agreementId: id,
      signatureProcessNumber: signatureNumber,
      pinCode: pinCode
    };
    return this.http.post(`${this.baseUrl}${this.apiEndpoint}/signature:complete`, body);
  }

  public resendSmsPinCode(id: Guid, signatureNumber: string) {
    const body = {
      agreementId: id,
      signatureProcessNumber: signatureNumber
    };
    return this.http.post(`${this.baseUrl}${this.apiEndpoint}/signature:resendSms`, body);
  }

  public GetPolicySummary(
    agreementId: Guid,
    context: Guid,
    email: string
  ): Observable<PolicySummaryResponse> {
    let filter = new HttpParams();

    if (context) {
      filter = filter.append('contextId', context.toString());
    }

    if (email) {
      filter = filter.append('email', email);
    }
    return this.http.get<PolicySummaryResponse>(
      `${this.baseUrl}${this.apiEndpoint}/V2/${agreementId}/summary`,
      {
        params: filter
      }
    );
  }

  public GetPolicyOverview(
    policyReference: string,
    context: Guid,
    email: string
  ): Observable<PolicyOverviewResponse> {
    let filter = new HttpParams();

    if (context) {
      filter = filter.append('contextId', context.toString());
    }

    if (email) {
      filter = filter.append('email', email);
    }
    return this.http.get<PolicyOverviewResponse>(
      `${this.baseUrl}${this.apiEndpoint}/${policyReference}/overview`,
      {
        params: filter
      }
    );
  }

  startEndorsementSignatureProcess(correlationId: Guid, phonePrefix: string, phoneNumber: string) {
    const body = {
      correlationId: correlationId,
      phonePrefix: phonePrefix,
      phoneNumber: phoneNumber
    };
    return this.http.post(`${this.baseUrl}${this.apiEndpoint}/endorsement/signature:start`, body);
  }

  completeEndorsementSignatureProcess(
    correlationId: Guid,
    signatureProcessNumber: string,
    pinCode: string
  ) {
    const body = {
      correlationId: correlationId,
      signatureProcessNumber: signatureProcessNumber,
      pinCode: pinCode
    };
    return this.http.post(
      `${this.baseUrl}${this.apiEndpoint}/endorsement/signature:complete`,
      body
    );
  }

  public getPolicyWait4SignEndorsementWithEmail(email: string): Observable<boolean> {
    let filter = new HttpParams();
    filter = filter.append('email', email);
    return this.http
      .head<boolean>(`${this.baseUrl}${this.apiEndpoint}/policyWait4SignEndorsementWithEmail`, {
        params: filter,
        observe: 'response'
      })
      .pipe(
        map(response => {
          if (response.status === 200) {
            return true;
          } else if (response.status === 204) {
            return false;
          } else {
            throw new Error(`Unexpected status code: ${response.status}`);
          }
        }),
        catchError(error => {
          console.error('Error:', error);
          return throwError('Error occurred during HTTP request.');
        })
      );
  }
}
